<template>
  <ScrollablePanel full-width>
    <DataTable
      :pt="{
        thead: {
          class: ['top-[59px] position-sticky z-10 bg-[--v3-surface-surface-100]'],
        },
        wrapper: {
          style: {
            overflow: 'visible',
          },
        },
      }"
      :value="isPending ? Array.from(Array(10).keys()).map(() => ({})) : keywords"
      unstyled
    >
      <Column class="w-8">
        <template #header>
          <DtColumn class="justify-center" name="#" />
        </template>
        <template #body="{ index }">
          <Skeleton v-if="isPending" />
          <div
            v-else
            class="v3-body-semibold-600-line-height-auto text-center text-[--v3-global-textcolor]"
          >
            {{ index + 1 }}
          </div>
        </template>
      </Column>
      <Column field="term">
        <template #header>
          <DtColumn
            :model-value="sortField === 'term' ? (sortOrder as 0 | 1 | -1) : 0"
            class="w-max"
            name="Keywords"
            sortable
            tooltip-text="The most popular Etsy Keyword search terms for the month you specified."
            @update:model-value="handleSort('term', $event)"
          />
        </template>
        <template #body="{ data }">
          <Skeleton v-if="isPending" />
          <div v-else class="flex items-center gap-x-3.5">
            <SaveStar
              :fetching="loadingKeywordsList"
              :has-default-list="hasDefaultKeywordList"
              :loading="togglingKeywordList.has(data.term.toLowerCase())"
              :model-value="keywordsInListSet.has(data.term.toLowerCase())"
              @update:model-value="(value: boolean) => toggleStarKeywordFromList(data.term.toLowerCase(), value)"
            >
              <AddSaveList :selected-keyword="data.term.toLowerCase()" />
            </SaveStar>
            <div class="flex items-center gap-x-0.5">
              <KeywordDraggable :keyword="data.term">
                <RouterLink
                  :title="data.keyword"
                  :to="{
                    path: '/keyword-tool',
                    query: {
                      country: 'USA',
                      keyword: data.term,
                      source: 'etsy',
                    },
                  }"
                  class="v3-body-semibold-600-line-height-auto text-[--v3-theme-primarycolor]"
                >
                  {{ data.term }}
                </RouterLink>
              </KeywordDraggable>
              <Icon
                v-if="data.is_popular"
                class="size-3.5 text-red-500"
                name="trending-fire"
                style="display: unset;"
              />
            </div>
          </div>
        </template>
      </Column>
      <Column class="w-40">
        <template #header>
          <DtColumn
            class="justify-center"
            name="Trend graph"
            tooltip-text="How the popularity of the keywords changed over the last 15 months. From this you can get a sense of seasonality and whether a keyword is becoming more or less popular."
          />
        </template>
        <template #body="{ data }">
          <Skeleton v-if="isPending" />
          <div v-else class="flex justify-center">
            <TrendChart :data="buildTrendChartData(data.trend ?? [])" />
          </div>
        </template>
      </Column>
      <Column class="w-32" field="searches">
        <template #header>
          <DtColumn
            :model-value="sortField === 'searches' ? (sortOrder as 0 | 1 | -1) : 0"
            class="justify-center"
            name="Average searches"
            sortable
            tooltip-text="This shows the 12-month average search count."
            width="200"
            @update:model-value="handleSort('searches', $event)"
          />
        </template>
        <template #body="{ data }">
          <Skeleton v-if="isPending" />
          <div v-else class="v3-body-semibold-600-line-height-auto text-end text-[--v3-global-textcolor]">
            {{ (typeof data.searches === 'number')? numberFormater.format(data.searches): (data.searches == null ? "-" : data.searches) }}
          </div>
        </template>
      </Column>
    </DataTable>
  </ScrollablePanel>
</template>

<script setup lang="ts">
import { parse } from 'date-fns';
import { storeToRefs } from 'pinia';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Skeleton from 'primevue/skeleton';
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import DtColumn from '@/common/components/atoms/DtColumn';
import Icon from '@/common/components/atoms/Icon';
import KeywordDraggable from '@/common/components/atoms/KeywordDraggable';
import ScrollablePanel from '@/common/components/atoms/ScrollablePanel';
import SaveStar from '@/common/components/molecules/SaveStar';
import TrendChart from '@/common/components/molecules/TrendChart';
import AddSaveList from '@/common/components/organisms/AddSaveList';
import { useKeywordListStore } from '@/common/stores/lists';
import { useSearchParams } from '@/modules/trending/composables';
import type { TrendReportResource } from '@/modules/trending/types';

//

interface Props {
  isPending?: boolean
  keywords: TrendReportResource[]
}

const props = defineProps<Props>();

const { searchParams, updateSearchParams } = useSearchParams();

const keywords = computed(() => {
  let _keywords = props.keywords;

  if ((searchParams.value.query?.length ?? 0) > 0) {
    _keywords = _keywords.filter(
      (keyword) => keyword.term.includes(searchParams.value.query.toLowerCase()),
    );
  }

  if (searchParams.value.sort_field === 'term') {
    _keywords = [..._keywords].sort((a, b) => {
      if (searchParams.value.sort_order === -1) {
        return a.term.localeCompare(b.term);
      }

      if (searchParams.value.sort_order === 1) {
        return b.term.localeCompare(a.term);
      }

      return 0;
    });
  }

  if (searchParams.value.sort_field === 'searches') {
    _keywords = [..._keywords].sort((a, b) => {
      if (searchParams.value.sort_order === -1) {
        return b.searches - a.searches;
      }

      if (searchParams.value.sort_order === 1) {
        return a.searches - b.searches;
      }

      return 0;
    });
  }

  return _keywords;
});
const sortField = computed(() => searchParams.value.sort_field);
const sortOrder = computed(() => searchParams.value.sort_order);

const numberFormater = new Intl.NumberFormat('en-US');

function buildTrendChartData(items: TrendReportResource['trend']) {
  return [...items].reverse().map((item) => ({
    date: parse(item.date.toString(), 'yyyyMM', new Date()),
    value: item.value,
  }));
}

function handleSort(sortField: 'searches' | 'term', sortOrder: 0 | -1 | 1) {
  updateSearchParams({
    sort_field: sortOrder === 0 ? undefined : sortField,
    sort_order: sortOrder === 0 ? undefined : sortOrder,
  });
}

//

const keywordListStore = useKeywordListStore();
const { keywordsInListSet, lastActiveListId, loadingKeywordsList, lists } = storeToRefs(keywordListStore);
const { getKeywordsList, getLists } = useKeywordListStore();

const hasDefaultKeywordList = computed(() => lists.value.length ? !!lastActiveListId.value : false);
const togglingKeywordList = ref(new Set<string>());

const toggleStarKeywordFromList = async (keyword: string, value: boolean) => {
  togglingKeywordList.value.add(keyword);

  if (value) {
    await keywordListStore.addKeywordToDefaultList(keyword);
  } else {
    await keywordListStore.removeKeywordFromLists(keyword);
  }

  togglingKeywordList.value.delete(keyword);
};

onMounted(() => {
  getKeywordsList();
  getLists();
});
</script>
